/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useState, useContext, useCallback, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import MLPAUMBCoveragesComponent from '../MLPAUMBCoveragesComponent/MLPAUMBCoveragesComponent';
import MLEditableCoveragesComponent from '../MLEditableCoveragesComponent/MLEditableCoveragesComponent';
import MLReadOnlyCoverageComponent from '../MLReadOnlyCoverageComponent/MLReadOnlyCoverageComponent';
import MLHOConditionsCoverageComponent from '../MLHOConditionsCoverageComponent/MLHOConditionsCoverageComponent';
import MLHOEditScheduledPropertyComponent from '../MLHOEditScheduledPropertyComponent/MLHOEditScheduledPropertyComponent';
import '@xengage/gw-platform-translations';
import metadata from './MLPUPEditCoveragesComponent.metadata.json5';
import styles from './MLPUPEditCoveragesComponent.module.scss';
import messages from './MLPUPEditCoveragesComponent.messages';

import { IntlContext } from '@jutro/legacy/locale';

const animalLiabilityExclusionCovCode = 'PUPAnimalLiabilityExclusion_ML';

function MLPUPEditCoveragesComponent(props) {
    const {
        model,
        loading,
        onCovChange,
        onGeneralChange,
        isDisplayOnly,
        editCoverage,
        showEditCoverage,
        filterData,
        totalCoverage,
        itemizedFees,
        totalFees,
        isQuoteStale
    } = props;

    const translator = useTranslator();
    const intl = useContext(IntlContext);

    const isQnB = appConfig.portalCode === 'QB';
    const isGpa = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';


    const [hasOptionalCoverages, setHasOptionalCoverages] = useState(false);
    const [hasExcludedCoverages, setHasExcludedCoverages] = useState(false);

    const paExcessCoverages = _.get(model, 'prsnlAutoExcessCoverages', []);
    const watercraftExcessCov = _.get(model, 'prsnlWatercraftExcessCoverages', []);
    const additionalCovs = _.get(model, 'additionalCoverages', []);
    // PUP Animal Liability terms should not be shown in Quote Page but they're still needed to be there when executing the update requests.
    const liabilityExclusionCovs = _.get(model, 'liabilityExclusions', []).map((ex) => {
        if(ex.code_ML !== animalLiabilityExclusionCovCode){
            return ex;
        }
        return {...ex, terms: []};
    });
    const polConditions = _.get(model, 'policyConditions', []);


    const isSelectedCoverage = useCallback((coverages) => {
        let returnVal = false;
        if (coverages && coverages.length > 0) {
            coverages.forEach((coverage) => {
                if (coverage.selected === true) {
                    returnVal = true;
                }
            });
        }
        return returnVal;
    }, []);

    const hasOptionalCoveragesFunc = useCallback((covs) => {
        const { prsnlAutoExcessCoverages, prsnlWatercraftExcessCoverages, additionalCoverages } = covs;

        return (isSelectedCoverage(prsnlAutoExcessCoverages) || isSelectedCoverage(prsnlWatercraftExcessCoverages) || isSelectedCoverage(additionalCoverages));
    }, [isSelectedCoverage]);

    const hasExcludedCoveragesFunc = useCallback((covs) => {
        const { liabilityExclusions, policyConditions } = covs;

        return (isSelectedCoverage(liabilityExclusions) || isSelectedCoverage(policyConditions));
    }, [isSelectedCoverage]);

    useEffect(() => {
        setHasOptionalCoverages(hasOptionalCoveragesFunc(model));
        setHasExcludedCoverages(hasExcludedCoveragesFunc(model));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model]);

    const formatAmountString = useCallback((amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const overrides = {
        umbrellaHeaderRow: {
            className: isDisplayOnly ? 'row vertical-spacing-20 flex-wrapper flex-end flex-space-between' : 'row vertical-spacing-20 flex-wrapper flex-end'
        },
        umbrellaHeaderH2: {
            content: isGpa ? translator(messages.umbrellaCoverage) : translator(messages.yourUmbrellaCoverage),
            className: isGpa ? 'section-header' : 'section-header margin-top-40'
        },
        umbrellaHeaderP: {
            visible: !isGpa && isDisplayOnly
        },
        editCovBtnDiv: {
            visible: !isDisplayOnly && isQnB
        },
        paUmbCovsDiv: {
            className: hasOptionalCoverages || hasExcludedCoverages || !isDisplayOnly ? 'vertical-spacing-40' : 'vertical-spacing-0'
        },
        paUmbCovsComponent: {
            coverages: _.get(model, 'primaryCoverages', []),
            editMode: !isDisplayOnly,
            onCovChange,
            isUmb: true,
            loading
        },
        optionalCoveragesDiv: {
            className: hasOptionalCoverages || !isDisplayOnly ? 'vertical-spacing-40' : ''
        },
        optionalCovsHeader: {
            visible: hasOptionalCoverages || !isDisplayOnly
        },
        prsnalAutoExcessCoverageComponentEdit: {
            visible: !isDisplayOnly,
            coverages: paExcessCoverages,
            loading,
            displayHelpMessage: true,
            onCovChange,
            allowSelectCoverage: true,
            filterData: filterData,
            showPremium: true
        },
        prsnalAutoExcessCoverageComponentReadonly: {
            visible: isDisplayOnly && isSelectedCoverage(paExcessCoverages),
            coverages: paExcessCoverages,
            loading,
            displayHelpMessage: true,
            onCovChange,
            filterData: filterData,
            showEditCoverage: !isDisplayOnly,
            isQuoteStale
        },
        prsnalWatercraftExcessCoverageComponentEdit: {
            visible: !isDisplayOnly,
            coverages: watercraftExcessCov,
            loading,
            displayHelpMessage: true,
            onCovChange,
            allowSelectCoverage: true,
            filterData: filterData
        },
        prsnalWatercraftExcessCoverageComponentReadonly: {
            visible: isDisplayOnly && isSelectedCoverage(watercraftExcessCov),
            coverages: watercraftExcessCov,
            loading,
            displayHelpMessage: true,
            onCovChange,
            filterData: filterData,
            showEditCoverage: !isDisplayOnly,
            isQuoteStale
        },
        addlCovsComponentEdit: {
            visible: !isDisplayOnly,
            coverages: additionalCovs,
            loading,
            displayHelpMessage: true,
            onCovChange,
            allowSelectCoverage: true,
            filterData: filterData
        },
        addlCovsComponentReadonly: {
            visible: isDisplayOnly && isSelectedCoverage(additionalCovs),
            coverages: additionalCovs,
            loading,
            displayHelpMessage: true,
            onCovChange,
            filterData: filterData,
            showEditCoverage: !isDisplayOnly,
            isQuoteStale
        },
        excludedCoveragesDiv: {
            visible: !isDisplayOnly || hasExcludedCoverages
        },
        liabilityExclusionsComponentEdit: {
            visible: !isDisplayOnly,
            coverages: liabilityExclusionCovs,
            loading,
            displayHelpMessage: true,
            onCovChange,
            allowSelectCoverage: true,
            filterData: filterData
        },
        liabilityExclusionsComponentReadonly: {
            visible: isDisplayOnly && isSelectedCoverage(liabilityExclusionCovs),
            coverages: liabilityExclusionCovs,
            loading,
            displayHelpMessage: true,
            onCovChange,
            filterData: filterData,
            showEditCoverage: !isDisplayOnly,
            isQuoteStale
        },
        policyConditionsComponentEdit: {
            visible: !isDisplayOnly,
            coverages: polConditions,
            loading,
            displayHelpMessage: true,
            onCovChange,
            allowSelectCoverage: true,
            filterData: filterData
        },
        policyConditionsComponentReadonly: {
            visible: isDisplayOnly && isSelectedCoverage(polConditions),
            coverages: polConditions,
            loading,
            displayHelpMessage: true,
            onCovChange,
            filterData: filterData,
            showEditCoverage: !isDisplayOnly,
            isQuoteStale
        },
        totalCoverageDiv: {
            visible: !!totalCoverage
        },
        totalCovHr: {
            visible: !isDisplayOnly
        },
        totalCovRow: {
            className: !isDisplayOnly ? 'vertical-spacing-20 margin-top-20' : 'vertical-spacing-20'
        },
        totalCoverageCostP: {
            content: formatAmountString(totalCoverage)
        },
        stateFeesComponent: {
            visible: _.get(totalFees, 'amount', 0) > 0,
            totalFees: totalFees,
            itemizedFees: itemizedFees,
            displayAlternativeTemplate: true
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onEditCoverage: editCoverage
        },
        resolveComponentMap: {
            mlpaumbcoveragescomponent: MLPAUMBCoveragesComponent,
            mleditablecoveragescomponent: MLEditableCoveragesComponent,
            mlreadonlycoveragecomponent: MLReadOnlyCoverageComponent,
            mlhoconditionscoveragecomponent: MLHOConditionsCoverageComponent,
            mlhoeditscheduledpropertycomponent: MLHOEditScheduledPropertyComponent
        },
        resolveClassNameMap: styles
    };


    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLPUPEditCoveragesComponent.propTypes = {
    model: PropTypes.shape({}),
    onCovChange: PropTypes.func,
    loading: PropTypes.bool,
    onGeneralChange: PropTypes.func,
    isDisplayOnly: PropTypes.bool,
    editCoverage: PropTypes.func,
    showEditCoverage: PropTypes.bool,
    filterData: PropTypes.func,
    totalCoverage: PropTypes.shape({}),
    itemizedFees: PropTypes.arrayOf(PropTypes.shape({})),
    totalFees: PropTypes.shape({}),
    isQuoteStale: PropTypes.bool
};


export default MLPUPEditCoveragesComponent;
